/**
 * Event Create Page
 * 
 * Page for creating new events
 * 
 * Requirements: 2.6
 */

import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useEventsStore from '../store/eventsStore';
import { restClient, CreateEventDto, UpdateEventDto, EventType, Location } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EventForm } from '../components/features/events/EventForm';

export function EventCreate() {
  const navigate = useNavigate();
  const { createEvent } = useEventsStore();

  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch event types and locations on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [types, locs] = await Promise.all([
          restClient.getEventTypes(),
          restClient.getLocations(),
        ]);
        
        // Sort event types alphabetically by name
        const sortedEventTypes = [...types].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setEventTypes(sortedEventTypes);
        
        // Sort locations alphabetically by name
        const sortedLocations = [...locs].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setLocations(sortedLocations);
        setLoading(false);
      } catch (err) {
        setError('Failed to load form data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = useCallback(async (data: CreateEventDto | UpdateEventDto) => {
    await createEvent(data as CreateEventDto);
    navigate('/events');
  }, [createEvent, navigate]);

  const handleCancel = useCallback(() => {
    navigate('/events');
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Event</h1>
        <p className="text-gray-600">Add a new cycling event</p>
      </div>

      <Card>
        <EventForm
          eventTypes={eventTypes}
          locations={locations}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
}

export default EventCreate;
